import { defineStore } from "pinia";
import {
  fetchAllHelpContent,
  upsertHelpArticle,
  upsertHelpOrgSection,
} from "@/api/helpContentApi";
import { useOrganizationStore } from "@/stores/organizationStore";
import { getAdministrator } from "@/data/getters";

/** Role keys used when editing Role-Specific tab content. */
export const HELP_ROLE_FIELDS = [
  { key: "provider", label: "Provider" },
  { key: "nurse", label: "Nurse" },
  { key: "patient", label: "Patient" },
  { key: "administrator", label: "Administrator" },
];

const ROLE_ALIASES = {
  administrator: ["administrator", "admin"],
  provider: ["provider", "doctor", "physician"],
  nurse: ["nurse", "registered nurse", "licensed practical nurse", "lpn"],
  patient: ["patient"],
};

/** Pull account-type string from session model when caller didn't pass one. */
function accountTypeFromAllData(allData) {
  if (!allData) return "";
  const candidates = [
    allData.manager?.accounttype,
    allData.manager?.account_type,
    allData.manager?.accountType,
    allData.patient?.accounttype,
    allData.patient?.account_type,
    allData.patient?.accountType,
    allData.user?.accounttype,
    allData.user?.account_type,
    allData.user?.accountType,
  ];
  for (const c of candidates) {
    const s = String(c || "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Map session account type → help_role_sections.role_name.
 * Returns "" when the type isn't one of the four editable roles (empty Role tab).
 */
export function normalizeHelpRole(accountType, allData) {
  if (getAdministrator(allData)) return "administrator";

  const raw = String(accountType || accountTypeFromAllData(allData) || "")
    .toLowerCase()
    .trim();
  if (!raw) {
    // Patient-only session with no typed role label.
    if (allData?.patient?.externId && !allData?.manager?.externid) return "patient";
    return "";
  }
  if (raw.includes("admin")) return "administrator";
  if (raw.includes("nurse") || raw.includes("lpn")) return "nurse";
  if (raw.includes("patient")) return "patient";
  if (
    raw.includes("provider") ||
    raw.includes("physician") ||
    raw.includes("doctor")
  ) {
    return "provider";
  }
  // Allied Professional, Home Health Aid, etc. → no role tab content.
  return "";
}

/** Find role-section HTML for the current user role ("" if none). */
export function pickRoleSectionHtml(roleSections, userRole) {
  if (!userRole || !Array.isArray(roleSections) || !roleSections.length) return "";
  const aliases = ROLE_ALIASES[userRole] || [userRole];
  for (const section of roleSections) {
    const name = String(section?.role_name || "").toLowerCase().trim();
    if (aliases.some((alias) => name === alias || name.includes(alias))) {
      return section.body_html || "";
    }
  }
  return "";
}

export function hasMeaningfulHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, "").trim().length > 0;
}

function indexByHelpId(rows, valueFn) {
  const map = {};
  for (const row of rows || []) {
    if (!row?.help_id) continue;
    map[row.help_id] = valueFn(row, map[row.help_id]);
  }
  return map;
}

/**
 * Pinia cache for contextual help articles + modal UI state.
 * Org tab label comes from organizationStore (accounts.organization).
 */
export const useHelpContentStore = defineStore("helpContent", {
  state: () => ({
    articlesById: {},
    roleSectionsByHelpId: {},
    orgSectionsByHelpId: {},
    loadedKey: "",
    loading: false,
    error: null,
    userRole: "",
    modalOpen: false,
    activeHelpId: "",
    activeTab: "",
  }),

  getters: {
    activeArticle(state) {
      return state.articlesById[state.activeHelpId] || null;
    },

    orgTabLabel() {
      return useOrganizationStore().orgSpecificLabel;
    },

    /** View-mode tabs: Overview → Org → Role → Details → How-To. */
    activeTabs(state) {
      const helpId = state.activeHelpId;
      if (!helpId) return [];
      const article = state.articlesById[helpId];
      const tabs = [];

      if (article && hasMeaningfulHtml(article.overview_html)) {
        tabs.push({ key: "overview", label: "Overview/Context", html: article.overview_html });
      }
      tabs.push({
        key: "org",
        label: this.orgTabLabel,
        html: state.orgSectionsByHelpId[helpId] || "",
      });
      tabs.push({
        key: "role",
        label: "Role-Specific",
        html: pickRoleSectionHtml(state.roleSectionsByHelpId[helpId] || [], state.userRole),
      });
      if (article && hasMeaningfulHtml(article.details_html)) {
        tabs.push({ key: "details", label: "Details", html: article.details_html });
      }
      if (article && hasMeaningfulHtml(article.how_to_html)) {
        tabs.push({ key: "howto", label: "How-To", html: article.how_to_html });
      }
      return tabs;
    },

    activeTabContent() {
      return this.activeTabs.find((t) => t.key === this.activeTab)?.html || "";
    },
  },

  actions: {
    setUserRole(accountType, allData) {
      this.userRole = normalizeHelpRole(accountType, allData);
    },

    /** Load help payload once per accountnumber (unless force).
     * Empty accountnumber is allowed (public pages): loads platform articles only. */
    async ensureLoaded(accountnumber, { force = false } = {}) {
      const accountKey = String(accountnumber || "").trim();
      const key = accountKey || "_platform";
      if (!force && this.loadedKey === key && Object.keys(this.articlesById).length) {
        return true;
      }
      if (this.loading) return false;

      this.loading = true;
      this.error = null;
      try {
        const data = await fetchAllHelpContent(accountKey);
        if (data?.status !== "success") {
          this.error = data?.message || "load_failed";
          return false;
        }
        this.articlesById = indexByHelpId(data.articles, (row) => row);
        this.roleSectionsByHelpId = indexByHelpId(data.roleSections, (row, prev) => {
          const list = prev || [];
          list.push(row);
          return list;
        });
        this.orgSectionsByHelpId = indexByHelpId(data.orgSections, (row) => row.body_html || "");
        this.loadedKey = key;
        return true;
      } catch (err) {
        this.error = err?.message || "load_failed";
        return false;
      } finally {
        this.loading = false;
      }
    },

    /** Open modal for helpId; ensures org + help caches are warm. */
    async openHelp(helpId, { accountnumber, accountType, allData } = {}) {
      // Always resolve role from session so Role-Specific shows the right copy.
      this.setUserRole(accountType, allData);
      if (accountnumber) await useOrganizationStore().ensureLoaded(accountnumber);
      await this.ensureLoaded(accountnumber);
      this.activeHelpId = helpId || "global";
      const tabs = this.activeTabs;
      this.activeTab = tabs.length ? tabs[0].key : "";
      this.modalOpen = true;
    },

    closeHelp() {
      this.modalOpen = false;
    },

    selectTab(tabKey) {
      this.activeTab = tabKey;
    },

    async savePlatformArticle(article) {
      const result = await upsertHelpArticle(article);
      if (result?.status !== "success") {
        throw new Error(result?.message || "Unable to save help article");
      }
      if (this.loadedKey) await this.ensureLoaded(this.loadedKey, { force: true });
      return result;
    },

    async saveOrgSection(accountnumber, helpId, bodyHtml, updatedBy) {
      const result = await upsertHelpOrgSection(accountnumber, helpId, bodyHtml, updatedBy);
      if (result?.status !== "success") {
        throw new Error(result?.message || "Unable to save organization help");
      }
      if (this.loadedKey) await this.ensureLoaded(this.loadedKey, { force: true });
      return result;
    },
  },
});
